import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  value: string; // "yyyy-MM-dd" ou ""
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  /** Navegação rápida por mês/ano via dropdown — ideal pra datas de nascimento */
  comSeletorDeAno?: boolean;
}

// Seletor de data próprio (não usa <input type="date">), pra garantir
// dd/mm/aaaa em qualquer navegador — o nativo depende do idioma do
// navegador/SO e no Chrome isso nem sempre segue o lang="pt-BR" da página.
export function DatePicker({ value, onChange, placeholder = "dd/mm/aaaa", className, disabled, id, comSeletorDeAno }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const valido = !!selected && isValid(selected);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal bg-background border-border h-11 px-3 hover:bg-background",
            !valido && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 size-4 shrink-0 opacity-60" />
          {valido ? format(selected as Date, "dd/MM/yyyy", { locale: ptBR }) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
        <Calendar
          mode="single"
          selected={valido ? selected : undefined}
          defaultMonth={valido ? selected : undefined}
          onSelect={(date) => {
            if (date) {
              onChange(format(date, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          locale={ptBR}
          autoFocus
          {...(comSeletorDeAno
            ? { captionLayout: "dropdown" as const, startMonth: new Date(1920, 0), endMonth: new Date() }
            : {})}
        />
      </PopoverContent>
    </Popover>
  );
}
